import { mapDeviceAuthenticationFromSysApi } from '@shure/shared/angular/data-access/device-authentication';
import {
	mapDeviceCompatibilityLevelFromSysApi,
	mapDeviceConferencingOperationModeFromSysApi,
	mapDeviceLicenseFromSysApi,
	mapDeviceLicenseWarningDetailsFromSysApi,
	mapDeviceModelFromSysApi,
	mapDeviceUsbConnectionStatusFromSysApi,
	mapFirmwareFromSysApi,
	mapMeteringModeFromSysApi,
	mapOutputModeFromSysApi,
	mapRfDensityModeFromSysApi,
	mapTransmitterMuteBehaviorFromSysApi,
	mapTransmitterUsbHostDisconnectMuteBehaviorFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import {
	ChannelGroup,
	DeviceRfDensityMode,
	DeviceTransmitterMuteBehavior,
	SpatialSeparation
} from '@shure/shared/angular/data-access/system-api/models';

import { DspDevice, DspDeviceChannel, DspDeviceMatrixmixer } from '../../api';
import { ChannelInfoChannelFragment } from '../graphql/generated/channel-info-channel.generated';
import {
	ChannelInfoDeviceFragment,
	ChannelInfoDeviceMatrixMixerFragment
} from '../graphql/generated/channel-info-device.generated';

import { mapChannelInfoChannelFromSysApi } from './channel-info-channel.mapper';

function mapEqualizerFiltersToIds(fragment: ChannelInfoChannelFragment): string[] {
	if (!fragment.description.features.equalizer.supported) {
		return [];
	}

	return (fragment.features.equalizer?.filters || []).map((f) => f.id);
}

function mapAudioChannels(fragment: ChannelInfoDeviceFragment): [DspDeviceChannel[], string[]] {
	if (!fragment.description.features.audioChannels.supported) {
		return [[], []];
	}

	let channels: DspDeviceChannel[] = [];
	const filterIds: string[] = [];
	for (const channelFragment of fragment.features.audioChannels?.audioChannels || []) {
		channels.push(mapChannelInfoChannelFromSysApi(channelFragment));
		filterIds.push(...mapEqualizerFiltersToIds(channelFragment));
	}

	if (channels.some((c) => !!c.features.channelBonding?.spatialSeparation)) {
		channels = mapChannelBondedUsbChannels(channels);
	}

	return [channels, filterIds];
}

export function mapMatrixmixer(fragment: ChannelInfoDeviceMatrixMixerFragment): [DspDeviceMatrixmixer, string[]] {
	if (!fragment.description.features.audioChannels.supported) {
		return [{}, []];
	}

	const channels = fragment.features.audioChannels?.audioChannels || [];
	const matrixmixer: DspDeviceMatrixmixer = {};
	const crosspointIds: string[] = [];

	for (const channel of channels) {
		if (!matrixmixer[channel.id]) {
			matrixmixer[channel.id] = [];
		}

		for (const crosspoint of channel.features.matrixMixerOutput?.crossPoints || []) {
			crosspointIds.push(crosspoint.id);

			if (!crosspoint.features.configuration?.enabled) {
				continue;
			}

			// id on StereoAudioChannel node is a faked id to create separate L/R from a single AudioChannel (for Matrix mixer view)
			// audioChannelId represents the true audioChannel for the StereoAudioChannel
			const inputChannelId =
				'isStereoAudioChannel' in crosspoint.inputChannel
					? crosspoint.inputChannel.audioChannelId
					: crosspoint.inputChannel.id;

			matrixmixer[channel.id].push(inputChannelId);

			if (!matrixmixer[inputChannelId]) {
				matrixmixer[inputChannelId] = [];
			}
			matrixmixer[inputChannelId].push(channel.id);
		}
	}

	return [matrixmixer, crosspointIds];
}

/**
 * Map device fragment to internal model & ids for subscribing to:
 * - Equalizer filters
 */
export function mapChannelInfoDeviceFromSysApi(
	fragment: ChannelInfoDeviceFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): DspDevice & { equalizerFilterIds: string[] } {
	const {
		description: { features, constraints }
	} = fragment;
	const [channels, equalizerFilterIds] = mapAudioChannels(fragment);
	const {
		features: {
			authentication,
			danteAudioNetworkAccess,
			audioChannelCount,
			license,
			stereoAudio,
			conferencingOperationMode,
			rfDensityMode,
			analogAudioOutputMode,
			allIntellimixBypass,
			transmitterMuteBehavior,
			usbConnection
		}
	} = fragment;

	return {
		id: fragment.id,
		compatibility: mapDeviceCompatibilityLevelFromSysApi(fragment.compatibility),
		status: fragment.status,
		model: mapDeviceModelFromSysApi(fragment.description.model),
		virtual: fragment.virtual,
		usbChannelsBonded:
			fragment.features.audioChannels?.audioChannels.some(
				(c) => c.description.features.channelBonding.supported
			) ?? false,
		equalizerFilterIds,
		constraints: {
			conferencingOperationMode: constraints.conferencingOperationMode
				? {
						options: constraints.conferencingOperationMode.mode.options.map((option) =>
							mapDeviceConferencingOperationModeFromSysApi(option)
						)
				  }
				: undefined,
			analogAudioOutputMode: {
				mode: {
					options:
						constraints.analogAudioOutputMode?.mode?.options.map((option) =>
							mapOutputModeFromSysApi(option)
						) ?? []
				}
			}
		},
		features: {
			logicMute: fragment.description.features.logicMute.supported ? fragment.features.logicMute : undefined,
			meteringMode: fragment.description.features.meteringModes.supported
				? {
						input: mapMeteringModeFromSysApi(fragment.features.meteringModes?.inputMode),
						output: mapMeteringModeFromSysApi(fragment.features.meteringModes?.outputMode)
				  }
				: undefined,
			audioChannels: channels,
			authentication: mapDeviceAuthenticationFromSysApi(authentication, authenticated, readonlyModeSelected),
			danteNetworkAccess: {
				changesAllowed: danteAudioNetworkAccess?.changesAllowed ?? true,
				factoryResetAllowed: danteAudioNetworkAccess?.factoryResetAllowed ?? true,
				isMissing: !features.danteAudioNetworkAccess.supported
			},
			firmware: mapFirmwareFromSysApi(fragment),
			license: features.license.supported
				? {
						...mapDeviceLicenseFromSysApi(license?.info, (v) =>
							mapDeviceLicenseWarningDetailsFromSysApi(v, audioChannelCount?.count ?? 0)
						),
						isMissing: license === null
				  }
				: undefined,
			stereoAudio: features.stereoAudio.supported
				? {
						enabled: stereoAudio?.enabled ?? false,
						isMissing: stereoAudio === null
				  }
				: undefined,
			conferencingOperationMode: features.conferencingOperationMode.supported
				? {
						mode: mapDeviceConferencingOperationModeFromSysApi(conferencingOperationMode?.mode),
						isMissing: !conferencingOperationMode
				  }
				: undefined,
			rfDensityMode: features.rfDensityMode.supported
				? {
						mode: rfDensityMode
							? mapRfDensityModeFromSysApi(rfDensityMode.mode)
							: DeviceRfDensityMode.Standard,
						isMissing: !rfDensityMode
				  }
				: undefined,
			analogAudioOutputMode: features.analogAudioOutputMode.supported
				? {
						mode: mapOutputModeFromSysApi(analogAudioOutputMode?.mode),
						isMissing: !analogAudioOutputMode
				  }
				: undefined,
			allDspBypass: features.allIntellimixBypass.supported
				? {
						bypassed: !!allIntellimixBypass?.bypassed,
						isMissing: !allIntellimixBypass
				  }
				: undefined,
			dectRfSpectrumScan: features.dectRfSpectrumScan.supported
				? {
						inProgress: fragment.features.dectRfSpectrumScan?.inProgress ?? false,
						isMissing: !fragment.features.dectRfSpectrumScan
				  }
				: undefined,
			transmitterMuteBehavior: features.transmitterMuteBehavior.supported
				? {
						usbHostDisconnectMuteBehavior: mapTransmitterUsbHostDisconnectMuteBehaviorFromSysApi(
							transmitterMuteBehavior?.usbHostDisconnectMuteBehavior
						),
						muteBehavior: transmitterMuteBehavior?.muteBehavior
							? mapTransmitterMuteBehaviorFromSysApi(transmitterMuteBehavior.muteBehavior)
							: DeviceTransmitterMuteBehavior.LocalMuteAll,
						isMissing: !transmitterMuteBehavior
				  }
				: undefined,
			usbConnection: features.usbConnection.supported
				? {
						status: mapDeviceUsbConnectionStatusFromSysApi(usbConnection?.status),
						isMissing: !usbConnection
				  }
				: undefined
		}
	};
}

// for channel bonded USB input/output channels, we only display the left channel
// but we will use a reference to the right channel ID for stereo metering
export function mapChannelBondedUsbChannels(channels: DspDeviceChannel[]): DspDeviceChannel[] {
	const usbInputLeftChannel = channels.find(
		(c) =>
			c.group === ChannelGroup.UsbInput && c.features.channelBonding?.spatialSeparation === SpatialSeparation.Left
	);
	const usbOutputLeftChannel = channels.find(
		(c) =>
			c.group === ChannelGroup.UsbOutput &&
			c.features.channelBonding?.spatialSeparation === SpatialSeparation.Left
	);
	if (!usbInputLeftChannel?.features.channelBonding || !usbOutputLeftChannel?.features.channelBonding) {
		return channels;
	}

	const usbInputRightChannel = channels.find(
		(c) =>
			c.group === ChannelGroup.UsbInput &&
			c.features.channelBonding?.spatialSeparation === SpatialSeparation.Right
	);
	const usbOutputRightChannel = channels.find(
		(c) =>
			c.group === ChannelGroup.UsbOutput &&
			c.features.channelBonding?.spatialSeparation === SpatialSeparation.Right
	);
	if (usbInputLeftChannel && usbInputRightChannel) {
		usbInputLeftChannel.features.channelBonding.bondedChannelId = usbInputRightChannel.id;
	}
	if (usbOutputLeftChannel && usbOutputRightChannel) {
		usbOutputLeftChannel.features.channelBonding.bondedChannelId = usbOutputRightChannel.id;
	}

	return channels.filter((c) => ![usbInputRightChannel?.id, usbOutputRightChannel?.id].includes(c.id));
}
